import { User } from '../types/alarm';
import { signInWithPopup, GoogleAuthProvider, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { alarmService } from '../services/firebaseService';
import { getAvatarInitialsFromEmail, getUiAvatarPlaceholderUrl } from './uiAvatarPlaceholder';

// Google 로그인 팝업 시 "Cross-Origin-Opener-Policy would block window.close" 경고 방지:
// Firebase Hosting(firebase.json)에 Cross-Origin-Opener-Policy: same-origin-allow-popups 헤더 설정됨.
// 로컬 개발 시에는 해당 경고가 나올 수 있으며, 로그인 자체는 정상 동작함.

// Firebase Google 인증 제공자 초기화
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const initializeGoogleAuth = (): void => {
  // Firebase Authentication을 사용하므로 별도 초기화 불필요
  // 이 함수는 호환성을 위해 유지
};

export const signInWithGoogle = async (): Promise<User> => {
  try {
    // Firebase Authentication을 사용한 구글 로그인
    const result = await signInWithPopup(auth, googleProvider);
    const firebaseUser = result.user;
    
    // 기존 프로필 정보 가져오기
    let userProfile = null;
    try {
      userProfile = await alarmService.getUserProfile(firebaseUser.uid);
    } catch (error) {
      console.log('프로필 정보 없음, 새 사용자로 처리');
    }
    
    // 기존에 저장된 사용자 데이터 확인 (로컬 스토리지에서)
    const existingUser = getStoredUser();
    const existingUserType = existingUser?.userType || userProfile?.userType || 'free';
    
    // User 객체 생성
    const user: User = {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      name: userProfile?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '사용자',
      picture:
        firebaseUser.photoURL ||
        getUiAvatarPlaceholderUrl(getAvatarInitialsFromEmail(firebaseUser.email), { size: 40 }),
      userType: existingUserType,
      currentClassId: existingUser?.currentClassId || userProfile?.currentClassId
    };
    
    return user;
  } catch (error: any) {
    console.error('Google 로그인 실패:', error);
    
    // 에러 코드에 따른 메시지 처리
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('로그인 창이 닫혔습니다.');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
    } else if (error.code === 'auth/network-request-failed') {
      throw new Error('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
    } else if (error.code === 'auth/unauthorized-domain') {
      throw new Error('인증되지 않은 도메인입니다. Firebase 콘솔에서 도메인을 확인해주세요.');
    }
    
    throw new Error(error.message || 'Google 로그인에 실패했습니다.');
  }
};

export const signOut = async (): Promise<void> => {
  try {
    // Firebase 로그아웃
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Firebase 로그아웃 실패:', error);
  }
  
  // 로컬 스토리지 정리
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
  localStorage.removeItem('rememberLogin');
  
  // 세션 데이터도 정리
  try {
    const { clearSessionData } = await import('./sessionManager');
    clearSessionData();
  } catch (error) {
    // sessionManager가 없는 경우 무시 (순환 참조 방지)
    console.log('세션 데이터 정리 스킵:', error);
  }
};

export const getStoredUser = (): User | null => {
  // 먼저 sessionStorage 확인 (브라우저 세션 동안만 유지)
  const sessionUserStr = sessionStorage.getItem('user');
  if (sessionUserStr) {
    return JSON.parse(sessionUserStr);
  }
  
  // localStorage 확인 (로그인 유지 옵션 사용 시)
  const localUserStr = localStorage.getItem('user');
  return localUserStr ? JSON.parse(localUserStr) : null;
};

export const storeUser = (user: User, rememberMe: boolean = true): void => {
  if (rememberMe) {
    // 로그인 유지: localStorage에 저장 (브라우저 종료해도 유지)
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('rememberLogin', 'true');
    sessionStorage.removeItem('user');
  } else {
    // 로그인 유지 안함: sessionStorage에 저장 (브라우저 종료 시 삭제)
    sessionStorage.setItem('user', JSON.stringify(user));
    localStorage.removeItem('user');
    localStorage.removeItem('rememberLogin');
  }
};

export const isRememberLoginEnabled = (): boolean => {
  return localStorage.getItem('rememberLogin') === 'true';
};
